import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

const parseCoord = (value) => {
  if (value === null || value === undefined) return NaN;
  const normalized = String(value).trim().replace(",", ".");
  const num = Number(normalized);
  return Number.isFinite(num) ? num : NaN;
};

const isValidLatLng = (lat, lng) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180;

const LeafletMap = ({
  items = [],
  selectedId = null,
  onSelect = null,
  height = 320,
  preferredCenter = null,
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerClusterRef = useRef(null);
  const markerRefs = useRef(new Map());

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const hasPreferredCenter =
      preferredCenter &&
      Number.isFinite(Number(preferredCenter.lat)) &&
      Number.isFinite(Number(preferredCenter.lng));
    const initialCenter = hasPreferredCenter
      ? [Number(preferredCenter.lat), Number(preferredCenter.lng)]
      : [-34.6037, -58.3816];
    const initialZoom = hasPreferredCenter ? 13 : 11;

    const map = L.map(mapContainerRef.current, {
      zoomControl: true,
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false,
    }).setView(initialCenter, initialZoom);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);

    const markerCluster = L.markerClusterGroup({
      chunkedLoading: true,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      disableClusteringAtZoom: 14,
    });
    map.addLayer(markerCluster);

    mapRef.current = map;
    markerClusterRef.current = markerCluster;

    setTimeout(() => map.invalidateSize(), 0);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      markerClusterRef.current = null;
      markerRefs.current = new Map();
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const cluster = markerClusterRef.current;
    if (!map || !cluster) return;

    cluster.clearLayers();
    markerRefs.current.clear();

    const points = items
      .map((item) => ({
        ...item,
        lat: parseCoord(item.latitude),
        lng: parseCoord(item.longitude),
      }))
      .filter((item) => isValidLatLng(item.lat, item.lng));

    if (!points.length) return;

    const bounds = [];
    points.forEach((item) => {
      try {
        const marker = L.circleMarker([item.lat, item.lng], {
          radius: selectedId && Number(item.id) === Number(selectedId) ? 8 : 6,
          weight: 2,
          color: "#0d6efd",
          fillColor: "#3b82f6",
          fillOpacity: 0.85,
        });
        marker.on("click", () => {
          if (onSelect) onSelect(item.id);
        });

        const addressText = [item.address, item.city, item.state].filter(Boolean).join(", ");
        marker.bindPopup(
          `<div style="min-width:190px">
            <div style="font-weight:600;margin-bottom:4px;">${item.name || "Comercio"}</div>
            <div style="font-size:12px;color:#4b5563;margin-bottom:6px;">${addressText || "Direccion no cargada"}</div>
            ${
              item.slug
                ? `<a href="/catalogo/${item.slug}" style="font-size:12px;">Ver beneficios</a>`
                : ""
            }
          </div>`
        );

        cluster.addLayer(marker);
        markerRefs.current.set(item.id, marker);
        bounds.push([item.lat, item.lng]);
      } catch (err) {
        console.warn("Marker invalido omitido:", item.id, item.lat, item.lng);
      }
    });

    try {
      map.stop();

      if (bounds.length === 1) {
        map.setView(bounds[0], 14, { animate: false });
      } else {
        map.fitBounds(bounds, { padding: [30, 30], animate: false, maxZoom: 14 });
      }
    } catch (err) {
      // Evita que una carrera de render/cleanup rompa toda la UI
      console.warn("Leaflet render race avoided:", err?.message || err);
    }
  }, [items, onSelect]);

  useEffect(() => {
    if (!markerRefs.current.size) return;

    markerRefs.current.forEach((marker, markerId) => {
      const isSelected = selectedId && Number(markerId) === Number(selectedId);
      marker.setStyle({
        radius: isSelected ? 8 : 6,
        weight: 2,
        color: isSelected ? "#1d4ed8" : "#0d6efd",
        fillColor: isSelected ? "#1d4ed8" : "#3b82f6",
        fillOpacity: 0.85,
      });
    });

    if (!selectedId) return;
    const selectedMarker = markerRefs.current.get(selectedId);
    if (!selectedMarker) return;
    selectedMarker.openPopup();
  }, [selectedId]);

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: "100%",
        height,
        borderRadius: 10,
      }}
    />
  );
};

export default LeafletMap;
